import { useEffect, useState } from 'react';
import type { CountryCode } from 'libphonenumber-js';
import { Tooltip } from '@/Components/ui/Tooltip';
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  detectCountry,
  formatForDisplay,
  nationalToE164,
  parsePhoneInput,
} from '@/utils/phone';

interface PhoneInputProps {
  value: string; // Format E.164: "+33606060606"
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  error?: string;
  className?: string;
  dataTestId?: string;
  size?: 'default' | 'sm';
  tooltip?: string;
}

export function PhoneInput({
  value,
  onChange,
  label = 'Téléphone',
  required,
  error,
  className = '',
  dataTestId,
  size = 'default',
  tooltip,
}: PhoneInputProps) {
  const [country, setCountry] = useState<CountryCode>(() => detectCountry(value) ?? DEFAULT_COUNTRY);
  const [display, setDisplay] = useState<string>(() => formatForDisplay(value, country));

  useEffect(() => {
    const incomingCountry = detectCountry(value);
    if (incomingCountry && incomingCountry !== country) {
      setCountry(incomingCountry);
    }
    if (value && nationalToE164(display, country) !== value) {
      setDisplay(formatForDisplay(value, incomingCountry ?? country));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleCountryChange = (code: CountryCode) => {
    setCountry(code);
    onChange(nationalToE164(display, code));
  };

  const handleInputChange = (input: string) => {
    const { display: nextDisplay, e164, country: nextCountry } = parsePhoneInput(input, country);
    setDisplay(nextDisplay);
    if (nextCountry !== country) setCountry(nextCountry);
    onChange(e164);
  };

  const hasValue = !!display;

  const sm = size === 'sm';
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const inputPadding = sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5';
  const labelFloated = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';
  const labelUnfloated = sm
    ? 'tw-top-[9px] tw-text-sm tw-text-gray-400 peer-focus:tw-top-1 peer-focus:tw-text-[10px] peer-focus:tw-text-affinicar-primary'
    : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px] peer-focus:tw-text-affinicar-primary';

  return (
    <div className={className}>
      <div
        className={`tw-relative tw-flex tw-items-center tw-border tw-rounded-xl tw-bg-white tw-transition-all
          ${error ? 'tw-border-red-500' : 'tw-border-gray-200'}
          focus-within:tw-border-affinicar-primary focus-within:tw-ring-2 focus-within:tw-ring-affinicar-primary/20`}
      >
        <select
          value={country}
          onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
          className={`tw-appearance-none tw-bg-transparent tw-border-0 tw-outline-none tw-pl-3 tw-pr-1 tw-py-0 ${textClass} tw-cursor-pointer`}
          style={{ maxWidth: '90px' }}
          data-testid={dataTestId ? `${dataTestId}-country` : undefined}
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.dialCode}
            </option>
          ))}
        </select>

        <div className={`tw-w-px ${sm ? 'tw-h-5' : 'tw-h-6'} tw-bg-gray-200 tw-mx-1 tw-flex-shrink-0`} />

        <input
          type="tel"
          value={display}
          onChange={(e) => handleInputChange(e.target.value)}
          required={required}
          placeholder={label || ' '}
          className={`tw-flex-1 tw-bg-transparent tw-border-0 tw-outline-none tw-px-2 ${inputPadding} ${textClass} tw-peer tw-placeholder-transparent`}
          data-testid={dataTestId}
          autoComplete="tel"
        />

        {label && (
          <label
            className={`tw-absolute tw-left-[95px] tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${hasValue ? `${labelFloated} tw-text-affinicar-primary` : labelUnfloated}
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}
      </div>
      {error && <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>}
    </div>
  );
}
